import React, { Suspense, useCallback, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { MenuTreeItem, MenuTreeProps } from "./types";
import { Loading } from "../Loading";
import MenuSearch from "./MenuSearch";
import { MenuItemComponent } from "./MenuItemComponent";

const MenuTree = ({
  data,
  onClickItemHandler,
  customMenuItemComponent,
  customComponent,
  activeItemId,
  className,
  variant = "default",
  placeholderText,
}: MenuTreeProps) => {
  const [treeData, setTreeData] = useState(data);

  function findPathToId(targetId: string): string[] {
    return data
      .map(node => findPathFromNode(node, targetId))
      .find(path => path !== null) ?? [];
  }

  function findPathFromNode(node: MenuTreeItem, targetId: string): string[] | null {
    if (node.id === targetId) return [node.id];

    for (const child of node.children ?? []) {
      const path = findPathFromNode(child, targetId);
      if (path) return [node.id, ...path];
    }

    return null;
  }

  const defaultOpenedItems =
    variant === "expanded" ? data.map((item) => item.id) : [];

  const [openedItemsIds, setOpenedItensIds] = useState<string[]>(defaultOpenedItems);

  useEffect(() => {
    if (!activeItemId) return

    setOpenedItensIds(findPathToId(activeItemId));
  }, [activeItemId])


  const onSelectItem = useCallback(
    (openedItens: string[], newTreeData: MenuTreeItem[]) => {
      setOpenedItensIds(openedItens);
      setTreeData(newTreeData);
    },
    []
  );
  useEffect(() => {
    setTreeData(data);
  }, [data]);

  return (
    <MenuWrapper className={className}>
      <Suspense fallback={<Loading />}>
        <MenuSearch
          data={data}
          onSelectItem={onSelectItem}
          placeHolderText={placeholderText}
        />
      </Suspense>

      {customComponent}
      <Suspense fallback={<Loading center />}>
        <StyledMenuItemsWrapper>
          {treeData.map((item) => (
            <MenuItemComponent
              item={item}
              onClickItemHandler={onClickItemHandler}
              depth={0}
              openedItemsIds={openedItemsIds}
              key={`item-level-0-${item.id}`}
              customMenuItemComponent={customMenuItemComponent}
              activeItemId={activeItemId}
              variant={variant}
            />
          ))}
        </StyledMenuItemsWrapper>
      </Suspense>
    </MenuWrapper>
  );
};

export default MenuTree;

const MenuWrapper = styled("div")(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  width: "100%",
  backgroundColor: theme.tokens.colors.gray[1],
  maxWidth: "340px",
  height: "calc(100vh - 2rem)",
  color: theme.tokens.colors.gray[7],
  display: "grid",
  gridTemplateRows: "auto auto 1fr",
  fontSize: "14px",

  ul: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    gridRow: 3,
  },
}));

const StyledMenuItemsWrapper = styled("ul")(({ theme }) => ({
  overflowY: "overlay" as "auto",
  height: "100%",
}));
